/**
 * Condition -> price multiplier (raw cards only).
 *
 * CLIENT TWIN of the multiplier join in public.revalue_inventory()
 * (migration `revalue_inventory_condition_multiplier`, 2026-09-05).
 * Keep the two in sync — exactly like VARIANT_PRIORITY / `prio` before it.
 * If they drift, the price the browser sweep writes won't match the price the
 * nightly job writes, and lots will flip back and forth between two values.
 *
 * The NUMBERS themselves are NOT in this file. They live in the
 * `condition_multipliers` table so Paul can tune them without a deploy.
 * This module only knows how to fetch and apply them.
 *
 * Safety rule: an unloadable multiplier table means we SKIP repricing rather
 * than guess. Writing a Near Mint price onto a Heavily Played card would
 * overstate the lot and then fight the nightly job for it.
 */
import { sb, CLOUD_USER } from "./supabase";

/** condition_grade (lowercased) -> multiplier. Null until a successful load. */
let cache: Map<string, number> | null = null;
let inflight: Promise<Map<string, number> | null> | null = null;

/**
 * Fetch and cache the multiplier table. Returns null if it can't be read —
 * callers must treat null as "don't reprice", not as "no discount".
 * Call once per sweep; the result is cached for the session.
 */
export async function loadConditionMultipliers(force = false): Promise<Map<string, number> | null> {
  if (cache && !force) return cache;
  if (inflight && !force) return inflight;

  inflight = (async () => {
    try {
      const { data, error } = await sb
        .from("condition_multipliers")
        .select("condition_grade,multiplier");
      if (error) throw error;

      const m = new Map<string, number>();
      for (const r of (data || []) as any[]) {
        const grade = String(r.condition_grade || "").trim();
        const mult = Number(r.multiplier);
        // Mirrors the CHECK constraint on the table: 0 < multiplier <= 1.
        if (grade && isFinite(mult) && mult > 0 && mult <= 1) {
          m.set(grade.toLowerCase(), mult);
        }
      }
      cache = m;
      return m;
    } catch (e) {
      console.warn("[conditionMultiplier] load failed:", e);
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/**
 * Multiplier for a stored Condition/Grade value.
 *   - blank condition            -> 1 (nothing to adjust)
 *   - table not loaded           -> null (caller must SKIP the lot)
 *   - grade absent from table    -> 1 (never silently discount an unknown)
 *
 * Graded slabs never reach here: they're excluded upstream by isGradedCard()
 * in the sweep and by the PSA/BGS/CGC/SGC filter in revalue_inventory().
 */
export function multiplierFor(conditionGrade: string | null | undefined): number | null {
  const grade = String(conditionGrade || "").trim();
  if (!grade) return 1;
  if (!cache) return null;
  const mult = cache.get(grade.toLowerCase());
  return mult == null ? 1 : mult;
}

/**
 * Suffix appended to Valuation Source so an estimate can never be mistaken for
 * market data — e.g. " × Moderately Played 0.78". Empty when no adjustment was
 * made. Byte-identical to the SQL:
 *     ' × ' || cond || ' ' || trim(to_char(mult, '0.00'))
 */
export function conditionSourceSuffix(
  conditionGrade: string | null | undefined,
  mult: number,
): string {
  const grade = String(conditionGrade || "").trim();
  if (!grade || !(mult > 0) || mult === 1) return "";
  return ` × ${grade} ${mult.toFixed(2)}`;
}

/* ============================================================
   Editing (Configs · Condition Pricing)
   ============================================================ */

export interface ConditionMultiplierRow { grade: string; multiplier: number; }

/** Same bounds as the table's CHECK constraint. Keep the two in step. */
export function isValidMultiplier(v: number): boolean {
  return isFinite(v) && v > 0 && v <= 1;
}

/**
 * Current values for a given list of grades, force-reloaded from the server.
 * Grades with no stored row come back as 1 (the no-adjustment default).
 * Returns null if the table can't be read at all, so the caller can say so
 * instead of showing a screen full of misleading 1.00s.
 */
export async function fetchConditionMultipliers(grades: string[]): Promise<ConditionMultiplierRow[] | null> {
  const m = await loadConditionMultipliers(true);
  if (!m) return null;
  return grades.map(g => ({ grade: g, multiplier: m.get(g.trim().toLowerCase()) ?? 1 }));
}

/**
 * Upsert edited multipliers and refresh the cache so the current session's
 * sweep uses them immediately. Rejects the whole batch if any value is out of
 * range rather than writing a partial set.
 */
export async function saveConditionMultipliers(rows: ConditionMultiplierRow[]): Promise<boolean> {
  if (!rows.length) return false;
  if (rows.some(r => !isValidMultiplier(r.multiplier))) return false;
  // RLS keys every row to the signed-in user; without one there is nothing to
  // write to and an upsert would fail the policy check anyway.
  if (!CLOUD_USER) { console.warn("[conditionMultiplier] not signed in"); return false; }
  const userId = CLOUD_USER.id;

  const payload = rows.map(r => ({
    user_id: userId,
    condition_grade: r.grade.trim(),
    multiplier: r.multiplier,
    updated_at: new Date().toISOString(),
  }));

  try {
    const { error } = await sb
      .from("condition_multipliers")
      .upsert(payload, { onConflict: "user_id,condition_grade" });
    if (error) throw error;
    await loadConditionMultipliers(true);   // repopulate cache from the server
    return true;
  } catch (e) {
    console.warn("[conditionMultiplier] save failed:", e);
    return false;
  }
}
